// WP-1.10 (M13 RC-1..4, S-18): recurring template queries/mutations.
import {
  recurringTemplateListResponseSchema,
  recurringTemplateResponseSchema,
  upcomingOccurrencesResponseSchema,
  type RecurringTemplateCreateRequest,
  type RecurringTemplateEditRequest,
  type RecurringTemplateResponse,
  type UpcomingOccurrence,
} from '@pfos/shared';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from './api';
import { ACCOUNTS_KEY } from './accounts';
import { TRANSACTIONS_KEY } from './transactions';

export const RECURRING_KEY = ['recurring'] as const;

export function useRecurringTemplates(
  filters: { status?: string } = {},
): UseQueryResult<RecurringTemplateResponse[]> {
  const params = new URLSearchParams();
  if (filters.status !== undefined) params.set('status', filters.status);
  const qs = params.toString();
  return useQuery({
    queryKey: [...RECURRING_KEY, filters],
    queryFn: async () => {
      const body = recurringTemplateListResponseSchema.parse(
        await api.get(`/api/v1/recurring${qs === '' ? '' : `?${qs}`}`),
      );
      return body.templates;
    },
  });
}

export function useUpcomingOccurrences(windowDays: number): UseQueryResult<UpcomingOccurrence[]> {
  return useQuery({
    queryKey: [...RECURRING_KEY, 'upcoming', windowDays],
    queryFn: async () => {
      const body = upcomingOccurrencesResponseSchema.parse(
        await api.get(`/api/v1/recurring/upcoming?windowDays=${String(windowDays)}`),
      );
      return body.occurrences;
    },
  });
}

function useInvalidateRecurring(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: RECURRING_KEY }),
      queryClient.invalidateQueries({ queryKey: TRANSACTIONS_KEY }), // posting may create one
      queryClient.invalidateQueries({ queryKey: ACCOUNTS_KEY }),
    ]);
  };
}

export function useCreateRecurringTemplate(): UseMutationResult<
  RecurringTemplateResponse,
  Error,
  RecurringTemplateCreateRequest
> {
  const invalidate = useInvalidateRecurring();
  return useMutation({
    mutationFn: async (input) =>
      recurringTemplateResponseSchema.parse(await api.post('/api/v1/recurring', input)),
    onSuccess: invalidate,
  });
}

export function useEditRecurringTemplate(
  id: string,
): UseMutationResult<RecurringTemplateResponse, Error, RecurringTemplateEditRequest> {
  const invalidate = useInvalidateRecurring();
  return useMutation({
    mutationFn: async (input) =>
      recurringTemplateResponseSchema.parse(await api.patch(`/api/v1/recurring/${id}`, input)),
    onSuccess: invalidate,
  });
}

export function usePauseRecurringTemplate(
  id: string,
): UseMutationResult<RecurringTemplateResponse, Error, void> {
  const invalidate = useInvalidateRecurring();
  return useMutation({
    mutationFn: async () =>
      recurringTemplateResponseSchema.parse(await api.post(`/api/v1/recurring/${id}/pause`)),
    onSuccess: invalidate,
  });
}

export function useResumeRecurringTemplate(
  id: string,
): UseMutationResult<RecurringTemplateResponse, Error, void> {
  const invalidate = useInvalidateRecurring();
  return useMutation({
    mutationFn: async () =>
      recurringTemplateResponseSchema.parse(await api.post(`/api/v1/recurring/${id}/resume`)),
    onSuccess: invalidate,
  });
}

export function useSkipRecurringOccurrence(
  id: string,
): UseMutationResult<RecurringTemplateResponse, Error, void> {
  const invalidate = useInvalidateRecurring();
  return useMutation({
    mutationFn: async () =>
      recurringTemplateResponseSchema.parse(await api.post(`/api/v1/recurring/${id}/skip`)),
    onSuccess: invalidate,
  });
}

export function usePostRecurringOccurrence(
  id: string,
): UseMutationResult<RecurringTemplateResponse, Error, number | undefined> {
  const invalidate = useInvalidateRecurring();
  return useMutation({
    mutationFn: async (amountMinor?: number) =>
      recurringTemplateResponseSchema.parse(
        await api.post(
          `/api/v1/recurring/${id}/post`,
          amountMinor !== undefined ? { amountMinor } : {},
        ),
      ),
    onSuccess: invalidate,
  });
}
